import React, { useState, useEffect } from 'react';
import { Label } from '../types/board';
import WordPressSiteUrlEditor from './WordPressSiteUrlEditor';
import { supabase } from '../services/supabase';
import './WordPressSiteConfig.css';

interface WordPressSiteConfigProps {
  allLabels: { [key: string]: Label };
  currentSiteLabel: string | null;
  onClose: () => void;
  onSave: (newSiteLabel: string) => void;
}

interface WordPressSiteData {
  label_id: string;
  wordpress_url: string;
  username: string;
  is_active: boolean;
}

const WordPressSiteConfig: React.FC<WordPressSiteConfigProps> = ({
  allLabels,
  currentSiteLabel,
  onClose,
  onSave,
}) => {
  const [selectedLabel, setSelectedLabel] = useState<string>(currentSiteLabel || '');
  const [wordPressSites, setWordPressSites] = useState<{ [labelId: string]: WordPressSiteData }>({});
  const [loading, setLoading] = useState(true);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);

  useEffect(() => {
    loadWordPressSites();
  }, []);

  const loadWordPressSites = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('wordpress_sites')
        .select('*');

      if (error) {
        console.error('Error loading WordPress sites:', error);
        return;
      }

      // Map by label_id for easy lookup
      const sitesMap: { [labelId: string]: WordPressSiteData } = {};
      data?.forEach((site: any) => {
        sitesMap[site.label_id] = {
          label_id: site.label_id,
          wordpress_url: site.wordpress_url,
          username: site.username,
          is_active: site.is_active,
        };
      });

      setWordPressSites(sitesMap);
    } catch (err) {
      console.error('Error loading WordPress sites:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (selectedLabel) {
      onSave(selectedLabel);
      onClose();
    }
  };

  const handleUrlEditorClose = () => {
    setEditingLabel(null);
    loadWordPressSites(); // Reload to get updated URLs
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="wp-config-overlay" onClick={onClose}>
      <div className="wp-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wp-config-header">
          <h3>WordPress Site Configuration</h3>
          <button className="wp-config-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="wp-config-content">
          <div className="wp-config-section">
            <p className="wp-config-label">Associate with Site Label:</p>
            <p className="wp-config-description">
              Select which site label should be linked to this WordPress instance.
              Only posts from this site will be synced.
            </p>

            <div className="wp-config-labels">
              {loading ? (
                <div className="loading-message">Loading WordPress sites...</div>
              ) : (
                Object.values(allLabels).map((label) => {
                  const wpSite = wordPressSites[label.id];
                  const hasUrl = !!wpSite?.wordpress_url;

                  return (
                    <div key={label.id} className="wp-config-label-row">
                      <label
                        className={`wp-config-label-option ${
                          selectedLabel === label.name ? 'selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="siteLabel"
                          value={label.name}
                          checked={selectedLabel === label.name}
                          onChange={(e) => setSelectedLabel(e.target.value)}
                        />
                        <span
                          className="label-dot"
                          style={{ backgroundColor: label.color }}
                        ></span>
                        <div className="label-info">
                          <span className="label-name">{label.name}</span>
                          {hasUrl && (
                            <span className="label-url">
                              {wpSite.wordpress_url}
                              {!wpSite.is_active && (
                                <span className="inactive-badge">Inactive</span>
                              )}
                            </span>
                          )}
                          {!hasUrl && (
                            <span className="label-url not-configured">
                              No WordPress URL configured
                            </span>
                          )}
                        </div>
                        {selectedLabel === label.name && (
                          <span className="selected-indicator">✓</span>
                        )}
                      </label>
                      <button
                        type="button"
                        className="wp-config-edit-url"
                        onClick={() => setEditingLabel(label)}
                        title={hasUrl ? 'Edit WordPress URL' : 'Add WordPress URL'}
                      >
                        {hasUrl ? '✏️' : '➕'}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="wp-config-note">
            <strong>Note:</strong> This setting is stored in your browser's localStorage.
            WordPress URLs and credentials are stored in the Supabase database.
            Click the ✏️ or ➕ button next to each site to add/edit its WordPress URL.
          </div>
        </div>

        <div className="wp-config-footer">
          <button className="wp-config-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="wp-config-save"
            onClick={handleSave}
            disabled={!selectedLabel}
          >
            Save Changes
          </button>
        </div>
      </div>

      {editingLabel && (
        <WordPressSiteUrlEditor
          label={editingLabel}
          onClose={handleUrlEditorClose}
          onSave={handleUrlEditorClose}
        />
      )}
    </div>
  );
};

export default WordPressSiteConfig;
